import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { blogsService } from '../../services/blogs';
import type { BlogPost } from '../../services/blogs';
import { useAuth } from '@/hooks/useAuth';
import { commentsService } from '../../services/comments';
import type { Comment } from '../../services/comments';
import Navbar from '../../components/feature/Navbar';
import LoadingSpinner from '../../components/feature/LoadingSpinner';
import ErrorMessage from '../../components/feature/ErrorMessage';

export default function BlogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [blogPost, setBlogPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);

  useEffect(() => {
    if (!id) {
      setError('ID de artículo no válido');
      setLoading(false);
      return;
    }

    const fetchBlogPost = async () => {
      try {
        setLoading(true);
        const post = await blogsService.getBlogPost(id);
        if (post) {
          setBlogPost(post);
        } else {
          setError('Artículo no encontrado');
        }
      } catch (err: any) {
        setError(err.message || 'Error al cargar el artículo');
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [id]);

  // Cargar comentarios cuando se muestra la sección
  useEffect(() => {
    if (showComments && id) {
      loadComments();
    }
  }, [showComments, id]);

  const loadComments = async () => {
    if (!id) return;
    
    try {
      setCommentsLoading(true);
      const fetchedComments = await commentsService.getCommentsByArticle(`blog_${id}`);
      setComments(fetchedComments);
    } catch (err) {
      console.error('Error loading comments:', err);
    } finally {
      setCommentsLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const handleGoBack = () => {
    navigate('/');
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      alert('Necesitas iniciar sesión para comentar');
      return;
    }

    if (!user?.isAffiliated) {
      alert('Solo usuarios afiliados pueden comentar');
      return;
    }

    if (!commentText.trim() || !id || !user) {
      return;
    }

    setCommentSubmitting(true);

    try {
      const newComment = {
        article_id: `blog_${id}`,
        text: commentText.trim(),
        author_id: user.id,
        author_name: user.fullName,
        is_affiliated: user.isAffiliated
      };

      await commentsService.createComment(newComment);
      setCommentText('');
      
      // Recargar comentarios
      await loadComments();
    } catch (err: any) {
      alert(err.message || 'Error al publicar el comentario');
    } finally {
      setCommentSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-lp-bg">
        <Navbar />
        <div className="pt-20">
          <LoadingSpinner message="Cargando artículo..." />
        </div>
      </div>
    );
  }

  if (error || !blogPost) {
    return (
      <div className="min-h-screen bg-lp-bg">
        <Navbar />
        <div className="pt-20 px-4">
          <ErrorMessage 
            message={error || 'Artículo no encontrado'} 
            onRetry={handleGoBack}
          />
        </div>
      </div>
    );
  }

  const sanitizedContent = DOMPurify.sanitize(blogPost.content);

  return (
    <div className="min-h-screen bg-lp-bg">
      <Navbar />
      
      <div className="pt-20 px-4 pb-8">
        {/* Back Button */}
        <button 
          onClick={handleGoBack}
          className="flex items-center gap-2 text-lp-link hover:text-lp-link-hover font-ui text-sm mb-6 transition-colors duration-200 vintage-paper-subtle px-3 py-1 rounded-[15%] hover:shadow-md"
        >
          <i className="ri-arrow-left-line"></i>
          Volver
        </button>

        {/* Article Header */}
        <div className="vintage-paper aged-paper p-6 mb-6 shadow-lg">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-xs font-ui text-sepia-600 vintage-paper-subtle px-3 py-1 rounded-[15%]">
              {blogPost.category}
            </span>
            <span className="text-xs text-amber-700 font-ui bg-amber-100/50 px-2 py-0.5 rounded-full">
              Blog Usuario
            </span>
            {blogPost.is_affiliated && (
              <span className="text-xs text-green-700 font-ui bg-green-100/50 px-2 py-0.5 rounded-full">
                Afiliado
              </span>
            )}
            <span className="text-xs text-sepia-500 font-ui vintage-paper-subtle px-2 py-1 rounded-[15%]">
              {formatDate(blogPost.created_at)}
            </span>
          </div>
          
          <h1 className="font-headings text-2xl text-sepia-900 mb-4 leading-tight">
            {blogPost.title}
          </h1>
          
          <div className="flex justify-between items-center text-sm text-sepia-600 font-ui border-t border-sepia-300/50 pt-4">
            <span>Por: <strong>{blogPost.author_name}</strong></span>
          </div>

          {/* Tags */}
          {blogPost.tags && blogPost.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-4 pt-4 border-t border-sepia-300/30">
              {blogPost.tags.map((tag, index) => (
                <span key={index} className="text-xs text-sepia-500 font-ui vintage-paper-subtle px-2 py-1 rounded-[10%]">
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Article Image */}
        <div className="mb-6 overflow-hidden shadow-lg vintage-paper relative">
          <img 
            src={blogPost.cover_image || `https://readdy.ai/api/search-image?query=vintage%20${blogPost.category.toLowerCase()}%20scene%2C%20classic%20literature%20atmosphere%2C%20antique%20books%20and%20manuscripts%2C%20sepia%20tones%2C%20warm%20lighting%2C%20scholarly%20environment%2C%20old%20library%20setting%2C%20leather-bound%20books%2C%20wooden%20desk%20with%20brass%20details%2C%20nostalgic%20literary%20aesthetic%2C%20aged%20paper%20textures%2C%20classic%20academic%20atmosphere%2C%20warm%20browns%20and%20cream%20colors%2C%20vintage%20writing%20scene&width=375&height=200&seq=blog_${blogPost.id}&orientation=landscape`}
            alt={blogPost.title}
            className="w-full"
            style={{ filter: 'sepia(65%) contrast(1.05) saturate(0.9)' }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              if (!target.src.includes('readdy.ai')) {
                 target.src = `https://readdy.ai/api/search-image?query=vintage%20${blogPost.category.toLowerCase()}%20scene%2C%20classic%20literature%20atmosphere%2C%20antique%20books%20and%20manuscripts%2C%20sepia%20tones%2C%20warm%20lighting%2C%20scholarly%20environment%2C%20old%20library%20setting%2C%20leather-bound%20books%2C%20wooden%20desk%20with%20brass%20details%2C%20nostalgic%20literary%20aesthetic%2C%20aged%20paper%20textures%2C%20classic%20academic%20atmosphere%2C%20warm%20browns%20and%20cream%20colors%2C%20vintage%20writing%20scene&width=375&height=200&seq=blog_${blogPost.id}&orientation=landscape`;
              }
            }}
          />
          {/* Overlay vintage sepia */}
          <div className="absolute inset-0 bg-sepia-800/38 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-sepia-400/23 via-transparent to-sepia-700/30"></div>
        </div>

        {/* Article Content */}
        <div className="vintage-paper aged-paper p-6 shadow-lg mb-6">
          {/* Excerpt */}
          {blogPost.excerpt && (
            <div className="prose prose-sepia max-w-none mb-6">
              <p className="text-lg font-body text-sepia-800 leading-relaxed italic border-l-4 border-sepia-400/50 pl-4 vintage-paper-subtle p-3 rounded-r-[10%]">
                {blogPost.excerpt}
              </p>
            </div>
          )}
          
          {/* Content */}
          <div 
            className="prose prose-sepia max-w-none text-sepia-700 font-body leading-relaxed [&>p]:mb-4"
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          />
        </div>

        {/* Author Information */}
        <div className="vintage-paper-subtle p-4 mb-6">
          <h3 className="font-headings text-sm text-sepia-800 mb-2">INFORMACIÓN DEL AUTOR</h3>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sepia-200/60 rounded-full flex items-center justify-center">
              <i className="ri-user-line text-sepia-700"></i>
            </div>
            <div>
              <p className="text-sm font-ui text-sepia-800">{blogPost.author_name}</p>
              <p className="text-xs font-ui text-sepia-600">
                {blogPost.is_affiliated ? 'Usuario Afiliado' : 'Miembro de la comunidad'}
              </p>
            </div>
          </div>
        </div>

        {/* Comments Section */}
        <div className="vintage-paper aged-paper p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-headings text-lg text-sepia-900">COMENTARIOS</h3>
            <button
              onClick={() => setShowComments(!showComments)}
              className="text-xs text-sepia-600 hover:text-sepia-800 font-ui transition-colors"
            >
              {showComments ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>

          {showComments && (
            <div className="space-y-4">
              {/* Comment Form */}
              {isAuthenticated ? (
                <form onSubmit={handleCommentSubmit} className="vintage-paper-subtle p-4 rounded-[10%]">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 bg-sepia-200/60 rounded-full flex items-center justify-center">
                      <i className="ri-user-line text-sepia-600 text-xs"></i>
                    </div>
                    <span className="text-xs text-sepia-700 font-ui">{user?.fullName}</span>
                    {user?.isAffiliated && (
                      <span className="text-xs text-amber-700 font-ui bg-amber-100/50 px-2 py-0.5 rounded-full">
                        Afiliado
                      </span>
                    )}
                  </div>
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder={user?.isAffiliated ? "Escribe tu comentario aquí..." : "Solo usuarios afiliados pueden comentar"}
                    disabled={!user?.isAffiliated || commentSubmitting}
                    className="w-full p-3 text-sm font-body text-sepia-800 bg-sepia-50/50 border border-sepia-300/50 rounded-[10%] resize-none focus:outline-none focus:ring-2 focus:ring-sepia-400/50 disabled:opacity-50 disabled:cursor-not-allowed"
                    rows={3}
                    maxLength={500}
                  />
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-sepia-500 font-ui">
                      {commentText.length}/500 caracteres
                    </span>
                    <button
                      type="submit"
                      disabled={!commentText.trim() || !user?.isAffiliated || commentSubmitting}
                      className="vintage-paper-subtle text-sepia-900 hover:bg-sepia-200/70 px-3 py-1 text-xs font-ui transition-all duration-300 rounded-[15%] hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      {commentSubmitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          Enviando...
                        </>
                      ) : (
                        'Comentar'
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="vintage-paper-subtle p-4 rounded-[10%] text-center">
                  <i className="ri-lock-line text-sepia-400 mb-2"></i>
                  <p className="text-sm text-sepia-600 font-ui">
                    Inicia sesión para comentar
                  </p>
                </div>
              )}

              {/* Comments List */}
              <div className="space-y-3 pt-4 border-t border-sepia-300/50">
                {commentsLoading ? (
                  <div className="text-center py-4">
                    <i className="ri-loader-4-line animate-spin text-xl text-sepia-400 mb-2"></i>
                    <p className="text-sm text-sepia-500 font-ui">Cargando comentarios...</p>
                  </div>
                ) : comments.length > 0 ? (
                  comments.map((comment) => (
                    <div key={comment.id} className="vintage-paper-subtle p-3 rounded-[10%]">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 bg-sepia-200/60 rounded-full flex items-center justify-center">
                          <i className="ri-user-3-line text-sepia-600 text-xs"></i>
                        </div>
                        <span className="text-xs font-ui text-sepia-700">{comment.author_name}</span>
                        {comment.is_affiliated && (
                          <span className="text-xs text-amber-700 font-ui bg-amber-100/50 px-2 py-0.5 rounded-full">
                            Afiliado
                          </span>
                        )}
                        <span className="text-xs text-sepia-500 font-ui">
                          {new Date(comment.created_at).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                      <p className="text-sm text-sepia-700 font-body pl-8">
                        {comment.text}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <i className="ri-chat-3-line text-2xl text-sepia-400 mb-2"></i>
                    <p className="text-sm text-sepia-500 font-ui">
                      No hay comentarios aún. ¡Sé el primero en comentar!
                    </p>
                  </div>
                )}

                <div className="text-center py-4 border-t border-sepia-300/30">
                  <p className="text-xs text-sepia-500 font-ui italic">
                    Solo usuarios afiliados pueden comentar
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Vintage Paper Texture Effect */}
        <div 
          className="fixed inset-0 pointer-events-none opacity-5 z-0"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=vintage%20paper%20texture%2C%20old%20book%20pages%2C%20antique%20parchment%20background%2C%20sepia%20tones%2C%20weathered%20paper%20with%20subtle%20stains%20and%20aging%20marks%2C%20literary%20atmosphere%2C%20warm%20beige%20and%20cream%20colors%2C%20high%20resolution%20texture%2C%20nostalgic%20feel%2C%20classic%20book%20aesthetic&width=375&height=812&seq=vintage_bg_blog_detail&orientation=portrait')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'multiply'
          }}
        />
      </div>
    </div>
  );
}
